import React, { useState, useEffect } from 'react';
import {
  faCircleUser,
  faClose,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ProductNotesModalProps, ServiceNote } from '@helpers/Models';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { ConfirmationToast } from '@components/ConfirmationToast';

export const ProductNotesModal: React.FC<ProductNotesModalProps> = ({
  isOpen,
  onClose,
  orderId,
  orderSpeciesId,
  orderProductId,
  showToastMessage,
  productNotes = [],
  mode = 'edit', // default to edit for backward compatibility
}) => {
  // Get the first note if it exists
  const initialNote = productNotes[0];
  const [note, setNote] = useState(initialNote?.note || '');
  const [editMode, setEditMode] = useState(!initialNote);
  const [isLoading, setIsLoading] = useState(false);
  const [currentNote, setCurrentNote] = useState<ServiceNote | null>(
    initialNote || null,
  );
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Update state when productNotes prop changes
  useEffect(() => {
    const updatedNote = productNotes[0];
    setCurrentNote(updatedNote || null);
    setNote(updatedNote?.note || '');
    setEditMode(!updatedNote && mode === 'edit');
  }, [productNotes, mode]);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSubmit = async () => {
    if (!note.trim()) return;

    setIsLoading(true);
    try {
      const payload = {
        orderId,
        orderSpeciesId,
        orderProductId,
        note,
        ...(currentNote && { id: currentNote.id }),
      };

      const response = await Axios.post(
        ApiEndpoints.PRODUCT_PROCESS_UPSERT_NOTES,
        payload,
      );

      setCurrentNote(response.data.data);

      showToastMessage(
        currentNote
          ? responseTexts.NOTES_UPDATE_SUCCESS
          : responseTexts.NOTES_ADDED_SUCCESS,
        'success',
      );
      onClose();
      setEditMode(false);
    } catch (error) {
      showToastMessage(errorMessage.NOTES_SAVE_ERROR, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  /**
   * Deletes a product note by its ID.
   *
   * Sends a POST request to the API endpoint to delete the note with the given `noteId`.
   * If the deletion is successful, it closes the confirmation modal and triggers the `onClose` callback.
   * If an error occurs during the request, an error toast message is displayed.
   *
   * @async
   * @param {number} noteId - The unique ID of the note to be deleted.
   * @returns {Promise<void>} A promise that resolves when the deletion process is complete.
   */
  const handleNotesDeletion = async (noteId: number): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.DELETE_PRODUCT_PROCESS_NOTES, {
        id: noteId,
      });
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-sm shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b align-baseline">
          <h2 className="text-lg font-semibold">
            {editMode
              ? currentNote
                ? 'Edit Note'
                : 'Add Note'
              : mode === 'view'
                ? 'View Note'
                : 'View Note'}
          </h2>
          <button
            type="button"
            className="ml-auto text-gray-500 hover:text-gray-700"
            onClick={onClose}
            title="Close"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        {/* View Mode: read-only, no edit/delete icons */}
        {!editMode && currentNote && mode === 'view' ? (
          <div className="bg-gray-100 p-4">
            <h2 className="text-lg font-semibold mb-4">
              Product Processing Notes
            </h2>
            <div className="bg-white p-4 rounded shadow min-h-14">
              <div className="flex justify-between">
                <span className="text-md break-words max-w-xl">
                  {currentNote.note}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                <FontAwesomeIcon
                  icon={faCircleUser}
                  className="text-gray-500 h-9"
                />
                <span className="font-medium">
                  {currentNote.createdBy},{' '}
                  {currentNote.createdAt instanceof Date
                    ? currentNote.createdAt.toLocaleString()
                    : currentNote.createdAt}
                </span>
              </div>
            </div>
          </div>
        ) : !editMode && currentNote ? (
          // Editable view: show edit/delete icons
          <div className="bg-gray-100 p-4">
            <h2 className="text-lg font-semibold mb-4">
              Product Processing Notes
            </h2>
            <div className="bg-white p-4 rounded shadow min-h-14">
              <div className="flex justify-between">
                <span className="text-md break-words max-w-xl">
                  {currentNote.note}
                </span>
                <div className="flex items-start gap-4 ml-4">
                  <button
                    type="button"
                    className="text-primary"
                    onClick={handleEdit}
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    type="button"
                    onClick={() => setShowConfirmation(true)}
                    className="text-red-600"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                <FontAwesomeIcon
                  icon={faCircleUser}
                  className="text-gray-500 h-9"
                />
                <span className="font-medium">
                  {currentNote.createdBy},{' '}
                  {currentNote.createdAt instanceof Date
                    ? currentNote.createdAt.toLocaleString()
                    : currentNote.createdAt}
                </span>
              </div>
            </div>
          </div>
        ) : mode === 'view' && !currentNote ? (
          // View mode with no notes: show message without buttons
          <div className="bg-gray-100 p-4">
            <h2 className="text-lg font-semibold mb-4">
              Product Processing Notes
            </h2>
            <div className="bg-white p-4 rounded shadow min-h-14 flex items-center justify-center">
              <span className="text-gray-500 text-center">
                No notes available for this product.
              </span>
            </div>
          </div>
        ) : (
          // Edit mode: show only the edit form
          <div className="bg-gray-100 p-4">
            <h2 className="text-lg font-semibold mb-4">
              Product Processing Notes
            </h2>
            <div className="bg-white p-4 rounded shadow min-h-14">
              <div className="flex flex-col gap-4">
                <textarea
                  className="w-full border rounded p-2 min-h-[100px] resize-none focus:outline-primary"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Enter products notes..."
                  autoFocus
                  disabled={mode === 'view'}
                />
                <div className="flex justify-between gap-2">
                  <button
                    type="button"
                    className="w-[50%] px-4 py-2 rounded border border-primary text-primary bg-white hover:bg-primary hover:text-white focus:outline-primary focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    onClick={() => {
                      if (currentNote) {
                        setEditMode(false);
                        setNote(currentNote.note);
                      } else {
                        onClose();
                      }
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  {mode !== 'view' && (
                    <button
                      type="button"
                      className="w-[50%] px-4 py-2 rounded bg-primary text-white disabled:opacity-50"
                      onClick={handleSubmit}
                      disabled={isLoading || !note.trim()}
                    >
                      {isLoading
                        ? 'Submitting...'
                        : currentNote
                          ? 'Save Changes'
                          : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Delete confirmation toast */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="w-full max-w-md">
            <ConfirmationToast
              title="Delete Note"
              message={responseTexts.NOTES_DELETE_CONFIRMATION}
              onClose={() => setShowConfirmation(false)}
              onAction={() =>
                currentNote && handleNotesDeletion(currentNote.id)
              }
              buttonName="Delete"
              type="delete"
            />
          </div>
        </div>
      )}
    </div>
  );
};
